import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DOCUMENTS_URL, DOCUMENTS_V2_URL } from '../contants';
import { EntityTypeExtend, SubmissionResponse } from '@data-access/submissions';
import { IResults } from '@shared/iris.interface';
import {
  DocumentAttribute,
  DocumentDetail,
  DocumentTaskRaw,
  DocumentTypeExtended,
  MimeType,
  RequestDocument,
  SubmittedDocumentsResponse,
  UpdateBulkDocument,
  UpdateDocument,
  UploadDocumentPayload,
} from '../types';
import { extractBlobAndFilename } from '@shared/utils/download.util';
import { map, Subject, takeUntil, tap } from 'rxjs';
import { constructFormDataParams } from '@shared/utils/form-data.util';
import { injectDispatch } from '@ngrx/signals/events';
import { TASK_EVENTS } from '@data-access/tasks/store';
import { filterValidTaskIds } from '../utils';

import { WORKSPACE_URLS } from '@data-access/shared';

@Injectable({
  providedIn: 'root',
})
export class DocumentsService {
  private readonly _http = inject(HttpClient);
  private _abortController$ = new Subject<void>();
  private _taskEvents = injectDispatch(TASK_EVENTS);

  getAttributeTypes(entityType: string) {
    return this._http.get<IResults<DocumentAttribute>>(
      `${DOCUMENTS_URL}attribute-type/?entity_type=${entityType}`,
    );
  }

  getMimeTypes(page: number = 1, pageSize: number = 100) {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('page_size', pageSize.toString());
    return this._http.get<IResults<MimeType>>(`${DOCUMENTS_URL}mime_type/`, {
      params,
    });
  }

  getSubmissionDocumentTypes({ workspaceId }: { workspaceId: string }) {
    const queryParams = new HttpParams().set('workspace_id', workspaceId);

    return this._http.get<IResults<DocumentTypeExtended>>(
      `${DOCUMENTS_V2_URL}type/`,
      {
        params: queryParams,
      },
    );
  }

  getOutcomeDocumentTypes({ workspaceId }: { workspaceId: string }) {
    const queryParams = new HttpParams()
      .set('workspace_id', workspaceId)
      .set('outcome', 'true');

    return this._http.get<IResults<DocumentTypeExtended>>(
      `${DOCUMENTS_V2_URL}type/`,
      {
        params: queryParams,
      },
    );
  }

  uploadDocuments(paramsVal: UploadDocumentPayload) {
    const formData = constructFormDataParams({
      document_type_id: paramsVal.documentTypeId,
      entity_id: paramsVal.entityId,
      entity_type: paramsVal.entityType,
      file: paramsVal.file,
      is_response: 'true',
      is_reviewer_document: 'false',
      task_ids: paramsVal.taskIds,
    });

    return this._http
      .post(`${DOCUMENTS_V2_URL}`, formData)
      .pipe(takeUntil(this._abortController$));
  }

  /**
   * Upload an outcome document for IBC submissions.
   * Uses the v1 endpoint which supports the is_outcome parameter.
   * @param file - The file to upload
   * @param documentTypeId - The document type ID
   * @param documentName - The name of the document
   * @param workspaceId - The workspace ID (used to set workspace_id on the document)
   * @param entityType - The entity type ('study', 'site', or 'workspace')
   * @param entityId - The entity ID (study ID, site ID, or workspace ID)
   */
  uploadOutcomeDocument(
    file: File,
    documentTypeId: string,
    documentName: string,
    workspaceId: string,
    entityType: 'study' | 'site' | 'workspace' = 'workspace',
    entityId?: string,
  ) {
    const params: Record<string, string | File> = {
      file,
      entity_id: entityId || workspaceId,
      entity_type: entityType,
      document_type_id: documentTypeId,
      name: documentName,
      is_outcome: 'true',
    };

    // Always include outcome_workspace_id so documents can be queried by workspace
    params['outcome_workspace_id'] = workspaceId;

    const formData = constructFormDataParams(params);

    return this._http.post<DocumentDetail>(`${DOCUMENTS_URL}`, formData);
  }

  cancelDocumentUpload() {
    this._abortController$.next();
    this._abortController$.complete();
    this._abortController$ = new Subject<void>();
  }

  /*
   * Get documents by entity ID and entity type
   * @param entityId - The ID of the entity
   * @param entityType - The type of the entity
   * @param params - Additional HTTP parameters
   */
  getDocumentsByEntity(
    entityId: string,
    entityType: EntityTypeExtend,
    params: HttpParams,
  ) {
    const updatedParams = params.set('page_size', '1000');
    return this._http.get<SubmittedDocumentsResponse>(
      `${DOCUMENTS_V2_URL}${entityType}/${entityId}/`,
      {
        params: updatedParams,
      },
    );
  }

  getWorkspaceDocumentTasks(workspaceId: string) {
    return this._http.get<Record<string, DocumentTaskRaw>>(
      `${WORKSPACE_URLS.V2}${workspaceId}/document_tasks/`,
    );
  }

  getDocumentById(documentId: string) {
    return this._http.get<DocumentDetail>(`${DOCUMENTS_V2_URL}${documentId}/`);
  }

  /**
   * Update a document by ID
   * @param documentId - The ID of the document to update
   * @param document - The document update payload
   * @param submission - Partial submission response for task list refresh
   */
  updateDocument(
    documentId: string,
    document: UpdateDocument,
    submission: Partial<SubmissionResponse>,
  ) {
    const _document = filterValidTaskIds(document);
    return this._http
      .put<DocumentDetail>(`${DOCUMENTS_V2_URL + documentId}/`, _document)
      .pipe(
        tap((response) => {
          this.loadTasksList({
            current_stage: submission.current_stage || '',
            id: submission.id || '',
          });
          return response;
        }),
      );
  }

  /**
   * Bulk update documents
   * @param bulkDocument - The bulk document update payload
   * @param submission - Partial submission response for task list refresh
   */
  updateBulkDocuments(
    bulkDocument: UpdateBulkDocument,
    submission: Partial<SubmissionResponse>,
  ) {
    const _bulkDocument = filterValidTaskIds(bulkDocument);
    return this._http
      .put<unknown>(`${DOCUMENTS_V2_URL}bulk_update/`, _bulkDocument)
      .pipe(
        tap((response) => {
          this.loadTasksList({
            id: submission.id || '',
            current_stage: submission.current_stage || '',
          });
          return response;
        }),
      );
  }

  loadTasksList(_submission: { id: string; current_stage: string }) {
    if (_submission.id) {
      this._taskEvents.getTaskList({
        submissionStage: _submission?.current_stage || '',
        workspaceID: _submission.id,
      });
    }
  }

  /**
   * Download document by ID
   */
  downloadDocument(documentId: string) {
    return this._http
      .get<Blob>(`${DOCUMENTS_V2_URL}${documentId}/download/`, {
        observe: 'response',
        responseType: 'blob' as 'json',
      })
      .pipe(
        map((response) => {
          return extractBlobAndFilename(response);
        }),
      );
  }

  /*
   * Delete document by ID
   * @param documentId - The ID of the document to delete
   */
  deleteDocument(
    workspaceId: string,
    currentStage: string,
    documentId: string,
  ) {
    return this._http
      .delete(`${WORKSPACE_URLS.V2}${workspaceId}/document/${documentId}/`)
      .pipe(
        tap((response) => {
          this.loadTasksList({
            id: workspaceId,
            current_stage: currentStage,
          });
          return response;
        }),
      );
  }

  bulkDeleteDocument(
    workspaceId: string,
    currentStage: string,
    params: HttpParams,
  ) {
    return this._http
      .delete(`${WORKSPACE_URLS.V2}${workspaceId}/document/`, {
        params: params,
      })
      .pipe(
        tap((response) => {
          this.loadTasksList({
            id: workspaceId,
            current_stage: currentStage,
          });
          return response;
        }),
      );
  }

  requestDocument(document: RequestDocument) {
    return this._http
      .post<'Success'>(
        `${WORKSPACE_URLS.V2}${document.workspace_id}/request-document/`,
        document,
      )
      .pipe(
        tap((response) => {
          this.loadTasksList({
            id: document.workspace_id || '',
            current_stage: document.current_stage || '',
          });
          return response;
        }),
      );
  }
  /**
   * Mark document as complete/incomplete
   */
  markCompleteDocument(documentId: string, complete: boolean) {
    return this._http.put<DocumentDetail>(
      `${DOCUMENTS_V2_URL}${documentId}/mark/`,
      {
        is_complete: complete,
      },
    );
  }
}
